/**
 * The chat's state machine, as a pure reducer so it can be tested without React or a browser.
 * Everything the panel renders is derived from this state; the hook (useChatSession) only
 * dispatches actions around network calls.
 */

import type { ChatCopy } from "./copy";

export type Role = "assistant" | "user";
export type Mode = "ai" | "form";
export type Phase = "idle" | "starting" | "ready" | "sending";
export type CardState = "hidden" | "offered" | "required" | "submitting" | "saved";

export interface ChatMessage {
  id: string;
  role: Role;
  text: string;
  /** Notices the widget itself adds (form mode, cap, saved) — styled like the assistant, never sent to the model. */
  notice?: boolean;
}

export interface ChatState {
  open: boolean;
  phase: Phase;
  conversationId: string | null;
  mode: Mode;
  turnsLeft: number;
  userTurns: number;
  messages: ChatMessage[];
  card: CardState;
  cardDismissed: boolean;
  cardError: string | null;
  error: string | null;
  ended: boolean;
  /** What the visitor said the note is about, for the links shown while Jorge reads it. */
  topic: string | null;
}

export type ChatAction =
  | { type: "open" }
  | { type: "close" }
  | { type: "start" }
  | { type: "started"; conversationId: string; mode: Mode; turnsLeft: number; notice: string | null }
  | { type: "start_failed"; notice: string }
  | { type: "send"; id: string; text: string }
  | { type: "reply"; id: string; text: string; turnsLeft: number }
  | { type: "form_mode"; id: string; notice: string; turnsLeft: number }
  | { type: "send_failed"; error: string }
  | { type: "require_card" }
  | { type: "dismiss_card" }
  | { type: "card_submit" }
  | { type: "card_saved"; id: string; text: string; topic?: string | null }
  | { type: "card_failed"; error: string }
  | { type: "ended" };

let counter = 0;
export function nextId(prefix = "m"): string {
  counter += 1;
  return `${prefix}${counter}`;
}

export function initialState(introText: string): ChatState {
  return {
    open: false,
    phase: "idle",
    conversationId: null,
    mode: "ai",
    turnsLeft: 8,
    userTurns: 0,
    messages: [{ id: nextId("intro"), role: "assistant", text: introText }],
    card: "hidden",
    cardDismissed: false,
    cardError: null,
    error: null,
    ended: false,
    topic: null,
  };
}

function notice(state: ChatState, id: string, text: string): ChatMessage[] {
  return [...state.messages, { id, role: "assistant", text, notice: true }];
}

// A card that already collected the visitor's details never comes back.
function requireCard(card: CardState): CardState {
  return card === "saved" || card === "submitting" ? card : "required";
}

export function reduce(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "open":
      return { ...state, open: true };
    case "close":
      return { ...state, open: false };
    case "start":
      return { ...state, phase: "starting", error: null };
    case "started": {
      const formMode = action.mode === "form";
      return {
        ...state,
        phase: "ready",
        conversationId: action.conversationId,
        mode: action.mode,
        turnsLeft: action.turnsLeft,
        messages: formMode && action.notice ? notice(state, nextId("n"), action.notice) : state.messages,
        card: formMode ? requireCard(state.card) : state.card,
      };
    }
    case "start_failed":
      // No conversation on the server; the card still works through the contact-form fallback.
      return {
        ...state,
        phase: "ready",
        mode: "form",
        messages: notice(state, nextId("n"), action.notice),
        card: requireCard(state.card),
      };
    case "send":
      return {
        ...state,
        phase: "sending",
        error: null,
        userTurns: state.userTurns + 1,
        messages: [...state.messages, { id: action.id, role: "user", text: action.text }],
      };
    case "reply": {
      const reply: ChatMessage = { id: action.id, role: "assistant", text: action.text };
      const messages = [...state.messages, reply];
      // Offered after the first answer (Jorge's call, 2026-09-22); never re-offered once dismissed.
      const offer = state.card === "hidden" && !state.cardDismissed && state.userTurns >= 1;
      return {
        ...state,
        phase: "ready",
        turnsLeft: action.turnsLeft,
        messages,
        card: offer ? "offered" : state.card,
      };
    }
    case "form_mode":
      return {
        ...state,
        phase: "ready",
        mode: "form",
        turnsLeft: action.turnsLeft,
        messages: notice(state, action.id, action.notice),
        card: requireCard(state.card),
      };
    case "send_failed":
      return { ...state, phase: "ready", error: action.error };
    case "require_card":
      return { ...state, card: requireCard(state.card), cardError: null };
    case "dismiss_card":
      return state.card === "offered" ? { ...state, card: "hidden", cardDismissed: true } : state;
    case "card_submit":
      return { ...state, card: "submitting", cardError: null };
    case "card_saved":
      return {
        ...state,
        card: "saved",
        cardError: null,
        topic: action.topic ?? state.topic,
        messages: notice(state, action.id, action.text),
      };
    case "card_failed":
      return { ...state, card: "required", cardError: action.error };
    case "ended":
      return { ...state, ended: true };
    default:
      return state;
  }
}

// ---- contact card validation (bilingual messages come from copy) -------------------------

export interface CardFields {
  name: string;
  email: string;
  phone: string;
  business: string;
  message: string;
  hp: string;
  /** The note's context when the assistant is away: chip ids, and a site to look at. All optional. */
  about: string;
  timing: string;
  site: string;
}

export type CardErrors = Partial<Record<"name" | "email" | "phone" | "site", string>>;

export const EMPTY_CARD: CardFields = { name: "", email: "", phone: "", business: "", message: "", hp: "", about: "", timing: "", site: "" };

/** A domain or a URL Jorge can open: "example.com", "www.example.com/menu", "https://maps.app.goo.gl/…". */
export const isValidSite = (value: string): boolean => /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/\S*)?$/i.test(value.trim());

/**
 * The note Jorge reads, as one message the Lambda already accepts: the two taps and the site as labelled
 * lines, then the visitor's own words. Nothing new for the Lambda or the CRM to store — the context rides in
 * the message, and the transcript travels beside it as before.
 */
export function composeNote(fields: CardFields, copy: ChatCopy): string {
  const about = copy.aboutChips.find((c) => c.id === fields.about)?.label;
  const timing = copy.timingChips.find((c) => c.id === fields.timing)?.label;
  const site = fields.site.trim();
  const lines = [
    about ? `${copy.noteLabels.about}: ${about}` : null,
    timing ? `${copy.noteLabels.timing}: ${timing}` : null,
    site ? `${copy.noteLabels.site}: ${site}` : null,
  ].filter((l): l is string => l !== null);
  const message = fields.message.trim();
  return [...lines, ...(message ? [lines.length ? "" : null, message].filter((l): l is string => l !== null) : [])].join("\n");
}

// Same shapes the Lambda enforces, so a value that passes here is accepted there.
export const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
export const isValidPhone = (value: string): boolean => /^[+]?[0-9\-\s().]{7,30}$/.test(value.trim());

export function validateCard(fields: CardFields, copy: ChatCopy): CardErrors {
  const errors: CardErrors = {};
  if (fields.name.trim().length < 2) errors.name = copy.errors.name;
  if (!isValidEmail(fields.email)) errors.email = copy.errors.email;
  if (fields.phone.trim() && !isValidPhone(fields.phone)) errors.phone = copy.errors.phone;
  if (fields.site.trim() && !isValidSite(fields.site)) errors.site = copy.errors.site;
  return errors;
}

/**
 * True when the composer should accept typing. Deliberately true while the conversation is still
 * starting: send() waits for it, and hiding the composer for that second showed "use the card
 * above" with no card on screen.
 */
export function canCompose(state: ChatState): boolean {
  return state.mode === "ai" && state.turnsLeft > 0;
}
